import React, { useMemo } from "react";
import { Box, Typography, Button, Divider } from "@mui/material";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import CancelIcon from "@mui/icons-material/Cancel";

const MarkupActivity = ({
  currentActivity,
  submitted,
  markupAnswers,
  setMarkupAnswers,
  selectedColor,
  typeToColorMap,
  setSelectedColor,
  preview = false, // <-- TRUE in author preview
}) => {
  const words =
    currentActivity?.Text?.split(" ")
      ?.map((w) => w.trim())
      ?.filter((w) => w !== "") || [];

  const isRTL = currentActivity?.Direction === "RTL";

  const normalizedOptions = useMemo(() => {
    const options = currentActivity?.Options || {};

    // If already numeric (student case), return as-is
    const firstKey = Object.keys(options)[0];
    if (!isNaN(parseInt(firstKey, 10))) {
      return options;
    }

    // Faculty case: word-based keys → numeric keys
    const words =
      currentActivity?.Text?.split(" ")
        ?.map((w) => w.trim())
        ?.filter(Boolean) || [];

    const result = {};
    Object.entries(options).forEach(([word, value]) => {
      const index = words.indexOf(word);
      if (index !== -1) {
        result[(index + 1).toString()] = value;
      }
    });

    return result;
  }, [currentActivity]);

  const boxWidth = 110;
  const boxHeight = 45;
  const insetStep = 5;

  // -----------------------
  // Disable interactions in PREVIEW
  // -----------------------
  const handleWordClick = (wordIndex) => {
    console.log(wordIndex);
    if (!selectedColor || submitted) return;

    const selectedType = Object.entries(typeToColorMap).find(
      ([type, color]) => color === selectedColor
    )?.[0];

    if (!selectedType) return;

    setMarkupAnswers((prev) => {
      const existing = prev[wordIndex] || [];
      const existingIndex = existing.findIndex(
        (entry) => entry.color === selectedColor
      );

      if (existingIndex !== -1) {
        const updated = existing.filter((_, i) => i !== existingIndex);
        return updated.length > 0
          ? { ...prev, [wordIndex]: updated }
          : Object.fromEntries(
              Object.entries(prev).filter(
                ([key]) => parseInt(key, 10) !== wordIndex
              )
            );
      }

      return {
        ...prev,
        [wordIndex]: [
          ...existing,
          { color: selectedColor, type: selectedType },
        ],
      };
    });
  };

  // -----------------------
  // STUDENT correctness (NOT for preview)
  // -----------------------
  const showResults = submitted && !preview;
  let isCorrect = false;
  isCorrect = Object.entries(normalizedOptions).every(
    ([optionIndex, optionsArray]) => {
      const wordIndex = parseInt(optionIndex, 10) - 1;
      const selectedEntries = markupAnswers[wordIndex] || [];
      if (!selectedEntries.length) return false;

      const userTypes = selectedEntries.map((e) => e.type).sort();
      const correctTypes = optionsArray.map((opt) => opt.Type).sort();

      return JSON.stringify(userTypes) === JSON.stringify(correctTypes);
    }
  );

  const resultBgColor = showResults
    ? isCorrect
      ? "#e8f5e9"
      : "#ffebee"
    : "transparent";

  return (
    <>
      <Box
        sx={{
          display: "flex",
          flexWrap: "wrap",
          gap: "20px",
          marginTop: "10px",
          direction: isRTL ? "rtl" : "ltr",
          fontFamily: "Traditional Arabic, serif",
        }}
      >
        {words.map((word, idx) => {
          const entries = preview
            ? markupAnswers[idx]?.length
              ? markupAnswers[idx] // <-- user applied colors
              : (currentActivity.Options?.[(idx + 1).toString()] || []).map(
                  (opt) => ({
                    color: typeToColorMap[opt.Type],
                    type: opt.Type,
                  })
                )
            : markupAnswers[idx] || [];

          return (
            <Box
              key={idx}
              sx={{
                display: "inline-flex",
                flexDirection: "column",
                alignItems: isRTL ? "flex-end" : "flex-start",
              }}
              onClick={() => handleWordClick(idx)}
            >
              <Box
                sx={{
                  position: "relative",
                  display: "inline-flex",
                  alignItems: "center",
                  justifyContent: "center",
                  width: boxWidth,
                  height: boxHeight,
                  cursor: preview
                    ? "default"
                    : submitted
                    ? "not-allowed"
                    : "pointer",
                }}
              >
                {entries
                  .slice()
                  .reverse()
                  .map((entry, i) => {
                    const inset = i * insetStep;
                    return (
                      <Box
                        key={i}
                        sx={{
                          position: "absolute",
                          top: inset,
                          left: inset,
                          right: inset,
                          bottom: inset,
                          backgroundColor: entry.color,
                          borderRadius: "10px",
                        }}
                      />
                    );
                  })}

                <Box
                  sx={{
                    position: "relative",
                    width: boxWidth - entries.length * 10,
                    height: boxHeight - entries.length * 10,
                    backgroundColor:
                      entries.length > 0 ? entries[0].color : "#f0f0f0",
                    borderRadius: "10px",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    fontSize: currentActivity?.style?.fontSize || "1.5rem",
                  }}
                >
                  {word}
                </Box>
              </Box>
            </Box>
          );
        })}
      </Box>
      {showResults && (
        <Box
          sx={{
            mt: 3,
            p: 2,
            borderRadius: "10px",
            backgroundColor: resultBgColor,
          }}
        >
          {isCorrect ? (
            <>
              <Box sx={{ display: "flex", justifyContent: "center" }}>
                <CheckCircleOutlineIcon sx={{ fontSize: 40, color: "green" }} />
              </Box>
              <Typography
                sx={{
                  textAlign: "center",
                  color: "green",
                  fontWeight: "bold",
                  mt: 1,
                }}
              >
                Correct Answer!
              </Typography>
            </>
          ) : (
            <>
              <Box sx={{ display: "flex", justifyContent: "center" }}>
                <CancelIcon sx={{ fontSize: 40, color: "red" }} />
              </Box>
              <Typography
                sx={{
                  textAlign: "center",
                  color: "red",
                  fontWeight: "bold",
                  mt: 1,
                }}
              >
                Incorrect Answer!
              </Typography>
            </>
          )}
        </Box>
      )}
    </>
  );
};

export default MarkupActivity;
